import React, { useState, useEffect } from 'react';
import { Form, FormField, FormVariable } from '@/firebase/formService';
import { ComplianceTemplate, complianceService, ReferenceDocument, uploadReferenceDocument } from '@/firebase/complianceService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { X, Plus, ChevronUp, ChevronDown, FileText, Image, Upload, HelpCircle, Search, Sparkles, Loader2 } from 'lucide-react';
import FormulaExamplesModal from './FormulaExamplesModal';
import FormulaTestModal from './FormulaTestModal';
import InlineTemplateForm from './InlineTemplateForm';
import { extractTableFormItems, isGeminiConfigured } from '@/services/geminiService';
import { translateFormFields } from '@/services/formTranslationService';

interface FormFormProps {
  form?: Form;
  forms?: Form[]; // List of all forms for "Copy From" dropdown
  onSubmit: (data: Partial<Form>) => void;
  onCancel: () => void;
}

const FormForm: React.FC<FormFormProps> = ({ form, forms = [], onSubmit, onCancel }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState<Partial<Form>>({
    name: '',
    description: '',
    layoutType: 'standard', // Default to standard layout
    variables: [],
    fields: [],
    templateId: '',
    locationIds: [],
    referenceDocuments: []
  });
  const [uploadingReference, setUploadingReference] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [isLocationSpecific, setIsLocationSpecific] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<FormField['type']>('text');
  const [newVariableName, setNewVariableName] = useState('');
  const [newVariableValue, setNewVariableValue] = useState<Record<number, string>>({});
  const [expandedVariables, setExpandedVariables] = useState<Set<number>>(new Set());
  const [newFieldOption, setNewFieldOption] = useState<Record<number, string>>({});
  const [copyFromId, setCopyFromId] = useState<string>('');
  const [showFormulaExamples, setShowFormulaExamples] = useState<number | string | null>(null); // Field index showing examples (number for standard, "table-{index}" for table)
  const [showFormulaTest, setShowFormulaTest] = useState<number | string | null>(null); // Field index showing test modal (number for standard, "table-{index}" for table)
  const [formulaErrors, setFormulaErrors] = useState<Record<number | string, string>>({}); // Field index -> error message (number for standard fields, "table-{index}" for table fields)
  const [showLayoutHelp, setShowLayoutHelp] = useState(false); // Show layout type help
  // Table form state
  const [newItemName, setNewItemName] = useState('');
  const [newTableFieldLabel, setNewTableFieldLabel] = useState('');
  const [newTableFieldType, setNewTableFieldType] = useState<FormField['type']>('number');
  // AI extraction state
  const [isExtractingItems, setIsExtractingItems] = useState(false);
  const [extractedItems, setExtractedItems] = useState<string[]>([]);
  const [extractionNotes, setExtractionNotes] = useState<string>('');
  const [showExtractedItems, setShowExtractedItems] = useState(false);
  // Inline template creation
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    loadTemplates();
    loadLocations();
    if (form) {
      // Convert legacy string[] variables to FormVariable[] if needed
      let variables: FormVariable[] = [];
      if (form.variables) {
        if (Array.isArray(form.variables) && form.variables.length > 0) {
          const firstItem = form.variables[0];
          if (typeof firstItem === 'string') {
            variables = (form.variables as unknown as string[]).map(name => ({
              name,
              possibleValues: []
            }));
          } else {
            variables = form.variables as FormVariable[];
          }
        }
      }
      
      // Ensure itemsDefinition is always static if it exists
      let itemsDefinition = form.itemsDefinition;
      if (itemsDefinition && itemsDefinition.type !== 'static') {
        // Convert legacy variable type to static (if variable values exist, use them as static items)
        itemsDefinition = {
          type: 'static',
          items: itemsDefinition.type === 'variable' && form.variables 
            ? (form.variables.find(v => (typeof v === 'string' ? v : v.name) === itemsDefinition?.variableName) as any)?.possibleValues || []
            : itemsDefinition.items || []
        };
      }
      
      const locationIds = form.locationIds || [];
      setIsLocationSpecific(locationIds.length > 0);
      
      setFormData({
        name: form.name,
        name_es: form.name_es, // Include Spanish name if exists
        description: form.description,
        layoutType: form.layoutType || 'standard',
        variables: variables,
        fields: form.fields || [],
        templateId: form.templateId || '',
        locationIds: locationIds,
        referenceDocuments: form.referenceDocuments || [],
        // Table form fields
        itemsDefinition: itemsDefinition,
        tableFields: form.tableFields || []
      });
    }
  }, [form]);

  // Auto-translate existing form when loaded (if missing Spanish translations)
  useEffect(() => {
    const autoTranslateExistingForm = async () => {
      if (!form || isTranslating) return;
      
      // Check if form needs translation
      const needsTranslation = 
        (form.name && !form.name_es) ||
        form.fields?.some(f => f.label && !f.label_es) ||
        form.tableFields?.some(f => f.label && !f.label_es) ||
        (form.itemsDefinition?.items && form.itemsDefinition.items.length > 0 && 
         (!form.itemsDefinition.items_es || 
          form.itemsDefinition.items_es.length !== form.itemsDefinition.items.length));
      
      if (needsTranslation) {
        setIsTranslating(true);
        try {
          // Translate missing fields
          const translationResult = await translateFormFields(form);
          
          if (translationResult.success && translationResult.data) {
            // Update formData with translations (user can see/edit them)
            setFormData(prev => ({
              ...prev,
              ...translationResult.data,
            }));
          }
        } catch (error) {
          console.error('Auto-translation failed:', error);
          // Fail silently - translations will happen on save
        } finally {
          setIsTranslating(false);
        }
      }
    };
    
    autoTranslateExistingForm();
  }, [form]);

  const loadTemplates = async () => {
    const result = await complianceService.getAllTemplates(currentOrganization?.id);
    if (result.success && result.data) {
      setTemplates(result.data);
      // Extract unique categories from templates
      const templateCategories = Array.from(new Set(result.data.map(t => t.category).filter(Boolean)));
      // Add predefined categories if not present
      const predefinedCategories = [
        'Fire Safety',
        'Life Safety',
        'Health & Safety',
        'Equipment Maintenance',
        'Building Maintenance',
        'Environmental',
        'Security',
        'Other'
      ];
      const allCategories = Array.from(new Set([...predefinedCategories, ...templateCategories]));
      setCategories(allCategories);
    }
  };

  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    // Fetch user's full data from Firestore to get locationIds and role
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          userRole = userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    
    try {
      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user?.id,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        setLocations(result.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleLocationToggle = (locationId: string) => {
    setFormData(prev => {
      const currentIds = prev.locationIds || [];
      const newIds = currentIds.includes(locationId)
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, locationIds: newIds };
    });
  };

  const handleLocationSpecificToggle = (checked: boolean) => {
    setIsLocationSpecific(checked);
    if (!checked) {
      // Clear locationIds when toggle is off
      setFormData(prev => ({ ...prev, locationIds: [] }));
    }
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please enter a form name');
      return;
    }
    if (!formData.templateId) {
      alert('Please select a compliance template');
      return;
    }
    // Validation: If location-specific toggle is enabled, must have at least one location selected
    if (isLocationSpecific && (!formData.locationIds || formData.locationIds.length === 0)) {
      alert('Please select at least one location');
      return;
    }
    // Validation based on layout type
    if (formData.layoutType === 'table') {
      // Table form validation
      if (!formData.itemsDefinition || !formData.itemsDefinition.items || formData.itemsDefinition.items.length === 0) {
        alert('Please define items for the table form');
        return;
      }
      if (!formData.tableFields || formData.tableFields.length === 0) {
        alert('Please add at least one table field (column)');
        return;
      }
    } else {
      // Standard form validation
      if (!formData.fields || formData.fields.length === 0) {
        alert('Please add at least one form field');
        return;
      }
    }
    
    // Step 1: Translate missing Spanish fields
    setIsTranslating(true);
    let formDataToSave = formData;
    try {
      const translationResult = await translateFormFields(formData);
      
      if (translationResult.success && translationResult.data) {
        formDataToSave = translationResult.data;
      } else {
        console.warn('Translation failed, saving with English only:', translationResult.error);
        // Continue with save even if translation fails
      }
    } catch (error) {
      console.error('Translation error:', error);
      // Continue with save even if translation fails
    } finally {
      setIsTranslating(false);
    }
    
    // Step 2: Clean up formData - ensure fields don't have undefined properties
    const cleanedFormData: Partial<Form> = {
      name: formDataToSave.name,
      ...(formDataToSave.name_es && { name_es: formDataToSave.name_es }), // Include Spanish name
      templateId: formDataToSave.templateId,
      layoutType: formDataToSave.layoutType || 'standard',
      ...(formDataToSave.description && { description: formDataToSave.description }),
      ...(formDataToSave.description_es && { description_es: formDataToSave.description_es }), // Include Spanish description
      // Standard form fields
      ...(formDataToSave.layoutType === 'standard' && {
        fields: formDataToSave.fields?.map(field => {
        const cleanedField: FormField = {
          label: field.label,
          ...(field.label_es && { label_es: field.label_es }), // Include Spanish label
          type: field.type,
          order: field.order,
          ...(field.required !== undefined && { required: field.required }),
          ...(field.placeholder && { placeholder: field.placeholder }),
          // Include options array for select/radio/checkbox types (even if empty)
          ...((field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && 
              { options: field.options || [] }),
          ...(field.defaultValue && { defaultValue: field.defaultValue }),
          // Include formula for calculated type
          ...(field.type === 'calculated' && field.formula && { 
            formula: field.formula 
          }),
          ...(field.validation && { validation: field.validation })
        };
        return cleanedField;
        }),
      }),
      // Table form fields
      ...(formDataToSave.layoutType === 'table' && {
        itemsDefinition: formDataToSave.itemsDefinition ? {
          type: 'static' as const,
          items: formDataToSave.itemsDefinition.items || [],
          ...(formDataToSave.itemsDefinition.items_es && { items_es: formDataToSave.itemsDefinition.items_es }) // Include Spanish items
        } : undefined,
        tableFields: formDataToSave.tableFields?.map(field => {
          const cleanedField: FormField = {
            label: field.label,
            ...(field.label_es && { label_es: field.label_es }), // Include Spanish label
            type: field.type,
            order: field.order,
            ...(field.required !== undefined && { required: field.required }),
            ...(field.placeholder && { placeholder: field.placeholder }),
            ...((field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && 
                { options: field.options || [] }),
            ...(field.defaultValue && { defaultValue: field.defaultValue }),
            ...(field.type === 'reference' && field.itemReferenceValues && { 
              itemReferenceValues: field.itemReferenceValues 
            }),
            ...(field.type === 'calculated' && field.formula && { 
              formula: field.formula 
            }),
            ...(field.validation && { validation: field.validation })
          };
          return cleanedField;
        }) || []
      }),
      // Always include variables array (even if empty) so Firestore can properly update it
      variables: formDataToSave.variables || [],
      // Include reference documents if any
      ...(formDataToSave.referenceDocuments && formDataToSave.referenceDocuments.length > 0 && { 
        referenceDocuments: formDataToSave.referenceDocuments 
      }),
      // Set locationIds to undefined if toggle is disabled (backward compatible), otherwise use selected locationIds
      locationIds: isLocationSpecific && formDataToSave.locationIds && formDataToSave.locationIds.length > 0
        ? formDataToSave.locationIds
        : undefined
    };
    
    onSubmit(cleanedFormData);
  };

  const addField = () => {
    if (!newFieldLabel.trim()) return;

    const newField: FormField = {
      label: newFieldLabel.trim(),
      type: newFieldType,
      order: (formData.fields?.length || 0) + 1,
      required: false
    };

    // Add options for select/radio/checkbox types
    if (newFieldType === 'select' || newFieldType === 'radio' || newFieldType === 'checkbox') {
      newField.options = [];
    }

    setFormData({
      ...formData,
      fields: [...(formData.fields || []), newField]
    });
    setNewFieldLabel('');
    setNewFieldType('text');
  };

  const removeField = (index: number) => {
    const updatedFields = formData.fields?.filter((_, i) => i !== index) || [];
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    setFormData({
      ...formData,
      fields: updatedFields
    });
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    if (!formData.fields) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;

    const updatedFields = [...formData.fields];
    [updatedFields[index], updatedFields[newIndex]] = [updatedFields[newIndex], updatedFields[index]];
    
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    
    setFormData({
      ...formData,
      fields: updatedFields
    });
  };

  const updateField = (index: number, updates: Partial<FormField>) => {
    if (!formData.fields) return;
    const updatedFields = [...formData.fields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setFormData({
      ...formData,
      fields: updatedFields
    });
  };

  const validateFormula = (fieldIndex: number, formula: string) => {
    if (!formula || !formData.fields) {
      setFormulaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldIndex];
        return newErrors;
      });
      return;
    }

    const errors: string[] = [];
    const field = formData.fields[fieldIndex];
    
    // Get available field labels (fields that come before this calculated field)
    const availableFields = formData.fields
      .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
      .map(f => f.label);
    
    // Check for field references - look for field labels in the formula
    // Simple check: look for quoted strings that might be field labels
    const fieldLabelPattern = /"([^"]+)"/g;
    const matches = formula.match(fieldLabelPattern);
    
    if (matches) {
      matches.forEach(match => {
        const fieldLabel = match.slice(1, -1); // Remove quotes
        if (!availableFields.includes(fieldLabel)) {
          errors.push(`Field "${fieldLabel}" not found. Available: ${availableFields.join(', ') || 'none'}`);
        }
      });
    }
    
    // Also check for unquoted field references (common pattern)
    availableFields.forEach(fieldLabel => {
      // Check if field label appears as a word boundary (not in quotes)
      const regex = new RegExp(`\\b${fieldLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');
      const unquotedMatches = formula.match(regex);
      if (unquotedMatches) {
        // Check if it's not inside quotes
        const beforeMatch = formula.substring(0, formula.indexOf(unquotedMatches[0]));
        const quoteCount = (beforeMatch.match(/"/g) || []).length;
        if (quoteCount % 2 === 0) {
          // Not inside quotes - this is a valid field reference
          // No error, this is fine
        }
      }
    });
    
    // Check IF statement syntax
    if (formula.includes('IF(')) {
      const ifRegex = /IF\s*\([^,]+,[^,]+,[^)]+\)/gi;
      if (!ifRegex.test(formula)) {
        errors.push('IF statement format: IF(condition, trueValue, falseValue)');
      }
    }
    
    // Check for balanced parentheses
    const openParens = (formula.match(/\(/g) || []).length;
    const closeParens = (formula.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      errors.push('Unbalanced parentheses');
    }
    
    // Check for balanced quotes
    const quotes = (formula.match(/"/g) || []).length;
    if (quotes % 2 !== 0) {
      errors.push('Unbalanced quotes');
    }
    
    setFormulaErrors(prev => {
      const newErrors = { ...prev };
      if (errors.length > 0) {
        newErrors[fieldIndex] = errors[0]; // Show first error
      } else {
        delete newErrors[fieldIndex];
      }
      return newErrors;
    });
  };

  const validateTableFormula = (fieldIndex: number, formula: string) => {
    if (!formula || !formData.tableFields) {
      setFormulaErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`table-${fieldIndex}`];
        return newErrors;
      });
      return;
    }

    const errors: string[] = [];
    const field = formData.tableFields[fieldIndex];
    
    // Get available field labels (table fields that come before this calculated field)
    const availableFields = formData.tableFields
      .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
      .map(f => f.label);
    
    // Check for field references
    const fieldLabelPattern = /"([^"]+)"/g;
    const matches = formula.match(fieldLabelPattern);
    
    if (matches) {
      matches.forEach(match => {
        const fieldLabel = match.slice(1, -1);
        if (!availableFields.includes(fieldLabel)) {
          errors.push(`Field "${fieldLabel}" not found. Available: ${availableFields.join(', ') || 'none'}`);
        }
      });
    }
    
    // Check IF statement syntax
    if (formula.includes('IF(')) {
      const ifRegex = /IF\s*\([^,]+,[^,]+,[^)]+\)/gi;
      if (!ifRegex.test(formula)) {
        errors.push('IF statement format: IF(condition, trueValue, falseValue)');
      }
    }
    
    // Check balanced parentheses
    const openParens = (formula.match(/\(/g) || []).length;
    const closeParens = (formula.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      errors.push('Unbalanced parentheses');
    }
    
    // Check balanced quotes
    const quotes = (formula.match(/"/g) || []).length;
    if (quotes % 2 !== 0) {
      errors.push('Unbalanced quotes');
    }
    
    setFormulaErrors(prev => {
      const newErrors = { ...prev };
      const errorKey = `table-${fieldIndex}`;
      if (errors.length > 0) {
        newErrors[errorKey] = errors[0];
      } else {
        delete newErrors[errorKey];
      }
      return newErrors;
    });
  };

  const addFieldOption = (fieldIndex: number) => {
    const option = newFieldOption[fieldIndex]?.trim();
    if (!formData.fields || !option) return;
    const updatedFields = [...formData.fields];
    const field = updatedFields[fieldIndex];
    if (field.options) {
      field.options = [...field.options, option];
    } else {
      field.options = [option];
    }
    setFormData({
      ...formData,
      fields: updatedFields
    });
    setNewFieldOption({ ...newFieldOption, [fieldIndex]: '' });
  };

  // Table form helper functions
  const addTableItem = () => {
    if (!newItemName.trim()) return;
    const currentItems = formData.itemsDefinition?.items || [];
    setFormData({
      ...formData,
      itemsDefinition: {
        type: 'static',
        items: [...currentItems, newItemName.trim()]
      }
    });
    setNewItemName('');
  };

  const removeTableItem = (index: number) => {
    const currentItems = formData.itemsDefinition?.items || [];
    const updatedItems = currentItems.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      itemsDefinition: {
        type: 'static',
        items: updatedItems
      }
    });
  };

  const moveTableItem = (index: number, direction: 'up' | 'down') => {
    const currentItems = formData.itemsDefinition?.items || [];
    if (!currentItems || currentItems.length === 0) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= currentItems.length) return;

    const updatedItems = [...currentItems];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    
    setFormData({
      ...formData,
      itemsDefinition: {
        type: 'static',
        items: updatedItems
      }
    });
  };

  const handleExtractItemsFromFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isWord = file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx');
    const isImage = file.type.startsWith('image/');

    if (!isPDF && !isWord && !isImage) {
      alert('Unsupported file type. Please upload PDF, Word, or image files.');
      event.target.value = '';
      return;
    }

    if (!isGeminiConfigured()) {
      alert('AI service is not configured. Please contact your administrator.');
      event.target.value = '';
      return;
    }

    setIsExtractingItems(true);
    setExtractedItems([]);
    setExtractionNotes('');

    try {
      const result = await extractTableFormItems(file);
      
      if (result.items && result.items.length > 0) {
        setExtractedItems(result.items);
        setExtractionNotes(result.confidenceNotes || '');
        setShowExtractedItems(true);
      } else {
        alert('No items were found in the document. Please try a different file or add items manually.');
      }
    } catch (error: any) {
      console.error('Error extracting items:', error);
      alert(`Failed to extract items: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExtractingItems(false);
      event.target.value = '';
    }
  };

  const handleUseExtractedItems = () => {
    if (extractedItems.length === 0) return;
    
    const currentItems = formData.itemsDefinition?.items || [];
    // Merge extracted items with existing items, avoiding duplicates
    const mergedItems = [...currentItems];
    extractedItems.forEach(item => {
      if (!mergedItems.includes(item)) {
        mergedItems.push(item);
      }
    });
    
    setFormData({
      ...formData,
      itemsDefinition: {
        type: 'static',
        items: mergedItems
      }
    });
    
    // Reset extraction state
    setExtractedItems([]);
    setExtractionNotes('');
    setShowExtractedItems(false);
  };

  const handleDiscardExtractedItems = () => {
    setExtractedItems([]);
    setExtractionNotes('');
    setShowExtractedItems(false);
  };

  const addTableField = () => {
    if (!newTableFieldLabel.trim()) return;
    const newField: FormField = {
      label: newTableFieldLabel.trim(),
      type: newTableFieldType,
      order: (formData.tableFields?.length || 0) + 1,
      required: false
    };
    if (newTableFieldType === 'select' || newTableFieldType === 'radio' || newTableFieldType === 'checkbox') {
      newField.options = [];
    }
    setFormData({
      ...formData,
      tableFields: [...(formData.tableFields || []), newField]
    });
    setNewTableFieldLabel('');
    setNewTableFieldType('number');
  };

  const removeTableField = (index: number) => {
    const updatedFields = formData.tableFields?.filter((_, i) => i !== index) || [];
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    setFormData({
      ...formData,
      tableFields: updatedFields
    });
  };

  const moveTableField = (index: number, direction: 'up' | 'down') => {
    if (!formData.tableFields) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.tableFields.length) return;

    const updatedFields = [...formData.tableFields];
    [updatedFields[index], updatedFields[newIndex]] = [updatedFields[newIndex], updatedFields[index]];
    
    updatedFields.forEach((field, i) => {
      field.order = i + 1;
    });
    
    setFormData({
      ...formData,
      tableFields: updatedFields
    });
  };

  const updateTableField = (index: number, updates: Partial<FormField>) => {
    if (!formData.tableFields) return;
    const updatedFields = [...formData.tableFields];
    updatedFields[index] = { ...updatedFields[index], ...updates };
    setFormData({
      ...formData,
      tableFields: updatedFields
    });
  };


  const removeFieldOption = (fieldIndex: number, optionIndex: number) => {
    if (!formData.fields) return;
    const updatedFields = [...formData.fields];
    const field = updatedFields[fieldIndex];
    if (field.options) {
      field.options = field.options.filter((_, i) => i !== optionIndex);
    }
    setFormData({
      ...formData,
      fields: updatedFields
    });
  };

  const addVariable = () => {
    if (!newVariableName.trim()) return;
    const newVar: FormVariable = {
      name: newVariableName.trim(),
      possibleValues: []
    };
    setFormData({
      ...formData,
      variables: [...(formData.variables || []), newVar]
    });
    setNewVariableName('');
    const newIndex = (formData.variables?.length || 0);
    setExpandedVariables(new Set([...expandedVariables, newIndex]));
  };

  const removeVariable = (index: number) => {
    const updatedVariables = formData.variables?.filter((_, i) => i !== index) || [];
    setFormData({
      ...formData,
      variables: updatedVariables
    });
    const newExpanded = new Set(expandedVariables);
    newExpanded.delete(index);
    setExpandedVariables(newExpanded);
  };

  const toggleVariableExpanded = (index: number) => {
    const newExpanded = new Set(expandedVariables);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedVariables(newExpanded);
  };

  const addVariableValue = (varIndex: number) => {
    const valueToAdd = newVariableValue[varIndex]?.trim();
    if (!valueToAdd) return;

    const updatedVariables = [...(formData.variables || [])];
    if (updatedVariables[varIndex]) {
      updatedVariables[varIndex] = {
        ...updatedVariables[varIndex],
        possibleValues: [...updatedVariables[varIndex].possibleValues, valueToAdd]
      };
      setFormData({
        ...formData,
        variables: updatedVariables
      });
      setNewVariableValue({ ...newVariableValue, [varIndex]: '' });
    }
  };

  const removeVariableValue = (varIndex: number, valueIndex: number) => {
    const updatedVariables = [...(formData.variables || [])];
    if (updatedVariables[varIndex]) {
      updatedVariables[varIndex] = {
        ...updatedVariables[varIndex],
        possibleValues: updatedVariables[varIndex].possibleValues.filter((_, i) => i !== valueIndex)
      };
      setFormData({
        ...formData,
        variables: updatedVariables
      });
    }
  };

  const handleVariableKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addVariable();
    }
  };

  const handleReferenceDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingReference(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        try {
          // Get owner_role from the associated compliance template if available
          let ownerRole: string | undefined;
          if (formData.templateId) {
            const template = templates.find(t => t.id === formData.templateId);
            ownerRole = template?.owner_role;
          }
          
          const referenceDoc = await uploadReferenceDocument(file, {
            templateId: formData.templateId,
            organizationId: currentOrganization?.id,
            uploadedBy: user
              ? { id: user.id, email: user.email, name: user.name }
              : undefined,
            title: file.name,
            source: 'web',
            additionalMetadata: ownerRole ? { owner_role: ownerRole } : undefined,
          });
          return referenceDoc;
        } catch (error: any) {
          console.error(`Error uploading ${file.name}:`, error);
          alert(`Failed to upload ${file.name}: ${error.message}`);
          return null;
        }
      });

      const uploadedDocs = (await Promise.all(uploadPromises)).filter(doc => doc !== null) as ReferenceDocument[];
      
      if (uploadedDocs.length > 0) {
        setFormData({
          ...formData,
          referenceDocuments: [...(formData.referenceDocuments || []), ...uploadedDocs]
        });
      }
    } catch (error) {
      console.error('Error uploading reference documents:', error);
    } finally {
      setUploadingReference(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const removeReferenceDocument = (index: number) => {
    setFormData({
      ...formData,
      referenceDocuments: formData.referenceDocuments?.filter((_, i) => i !== index)
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    // Create a fake event object compatible with handleReferenceDocumentUpload
    const fakeEvent = {
      target: {
        files: files,
        value: ''
      }
    } as React.ChangeEvent<HTMLInputElement>;

    await handleReferenceDocumentUpload(fakeEvent);
  };

  const fieldTypes: FormField['type'][] = ['text', 'number', 'date', 'email', 'tel', 'textarea', 'select', 'checkbox', 'radio', 'photo', 'reference', 'calculated'];

  return (
    <div className="fixed inset-y-0 right-0 w-[600px] bg-white shadow-2xl border-l border-gray-200 z-[120] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-800">
          {form ? 'Edit Form' : 'Add New Form'}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 p-1"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <form id="form-form" onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Copy From (only show when adding new form) */}
          {!form && forms.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Copy From (Optional)
              </label>
              <select
                value={copyFromId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  setCopyFromId(selectedId);
                  if (selectedId) {
                    const sourceForm = forms.find(f => f.id === selectedId);
                    if (sourceForm) {
                      // Convert variables if needed
                      let variables: FormVariable[] = [];
                      if (sourceForm.variables) {
                        if (Array.isArray(sourceForm.variables) && sourceForm.variables.length > 0) {
                          const firstItem = sourceForm.variables[0];
                          if (typeof firstItem === 'string') {
                            variables = (sourceForm.variables as unknown as string[]).map(name => ({
                              name,
                              possibleValues: []
                            }));
                          } else {
                            variables = sourceForm.variables as FormVariable[];
                          }
                        }
                      }
                      
                      setFormData({
                        name: '', // Clear name so user enters new one
                        description: sourceForm.description || '',
                        layoutType: sourceForm.layoutType || 'standard',
                        variables: variables,
                        fields: sourceForm.fields ? [...sourceForm.fields] : [],
                        templateId: sourceForm.templateId || '',
                        referenceDocuments: sourceForm.referenceDocuments ? [...sourceForm.referenceDocuments] : [],
                        itemsDefinition: sourceForm.itemsDefinition,
                        tableFields: sourceForm.tableFields ? [...sourceForm.tableFields] : []
                      });
                    }
                  } else {
                    // Reset form when "None" is selected
                    setFormData({
                      name: '',
                      description: '',
                      layoutType: 'standard',
                      variables: [],
                      fields: [],
                      templateId: '',
                      referenceDocuments: [],
                      itemsDefinition: undefined,
                      tableFields: []
                    });
                  }
                }}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              >
                <option value="">None - Create New</option>
                {forms.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Row 1: Name and Template */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Form name"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Compliance <span className="text-red-600">*</span>
              </label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <select
                    value={formData.templateId || ''}
                    onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                    className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select...</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setIsCreatingTemplate(true)}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors whitespace-nowrap flex items-center gap-1 flex-shrink-0 font-medium"
                    title="Create a new compliance template"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Create New</span>
                  </button>
                </div>
              </div>
              {templates.length === 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-2 mt-2">
                  <p className="text-xs text-orange-800">
                    <strong>No templates available.</strong> Click "Create New" to create a compliance template, or go to Compliance Management to create one first.
                  </p>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mt-2">
                <p className="text-xs text-blue-800">
                  <strong>Why this is important:</strong> Linking a compliance template to your form ensures your company has a policy in place to periodically review form completion and data collection. This helps maintain accountability, track progress over time, and ensures compliance requirements are consistently met through structured data capture.
                </p>
              </div>
            </div>
          </div>

          {/* Location Selection */}
          <div className="mb-4 p-4 border border-gray-300 rounded-lg">
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={isLocationSpecific}
                onChange={(e) => handleLocationSpecificToggle(e.target.checked)}
                className="w-4 h-4 border-gray-300 rounded text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Available only to specific locations
              </span>
            </label>

            {isLocationSpecific && (
              <div className="mt-3 space-y-3">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search locations..."
                    value={locationSearchTerm}
                    onChange={(e) => setLocationSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>

                {/* Location Checkboxes */}
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
                  {filteredLocations.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2 text-center">No locations found</p>
                  ) : (
                    filteredLocations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(formData.locationIds || []).includes(location.id || '')}
                          onChange={() => handleLocationToggle(location.id || '')}
                          className="w-4 h-4 border-gray-300 rounded text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </label>
                    ))
                  )}
                </div>

                {/* Selected Locations Display */}
                {(formData.locationIds || []).length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-600 mb-2">Selected locations:</p>
                    <div className="flex flex-wrap gap-2">
                      {(formData.locationIds || [])
                        .map(locationId => locations.find(loc => loc.id === locationId))
                        .filter(Boolean)
                        .map((location) => (
                          <span
                            key={location!.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs"
                          >
                            {location!.name}
                            <button
                              type="button"
                              onClick={() => handleLocationToggle(location!.id || '')}
                              className="hover:text-purple-900"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Row 2: Layout Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
              Layout Type
              <button
                type="button"
                onClick={() => setShowLayoutHelp(!showLayoutHelp)}
                className="text-gray-400 hover:text-primary-600 transition-colors"
                title="Click for help"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
            </label>
            <select
              value={formData.layoutType || 'standard'}
              onChange={(e) => {
                const newLayoutType = e.target.value as 'standard' | 'table';
                setFormData({ 
                  ...formData, 
                  layoutType: newLayoutType,
                  // Clear table-specific fields if switching to standard
                  ...(newLayoutType === 'standard' && {
                    itemsDefinition: undefined,
                    tableFields: [],
                    headerFields: []
                  }),
                  // Clear standard fields if switching to table
                  ...(newLayoutType === 'table' && {
                    fields: []
                  })
                });
              }}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            >
              <option value="standard">Standard (Vertical Fields)</option>
              <option value="table">Table/Grid (Items as Rows)</option>
            </select>
            
            {/* Layout Type Help Modal */}
            {showLayoutHelp && (
              <div className="mt-2 bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="flex items-start gap-2">
                  <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 text-xs text-blue-900">
                    <p className="font-semibold mb-2">Layout Type Guide</p>
                    <div className="space-y-2">
                      <div>
                        <p className="font-medium text-blue-800 mb-1">Standard Layout (Vertical Fields):</p>
                        <ul className="list-disc ml-4 space-y-0.5 text-blue-700">
                          <li>Fields are displayed vertically, one after another</li>
                          <li>Each field takes full width of the form</li>
                          <li>Best for: Single-subject forms, surveys, checklists, general data collection</li>
                          <li>Each form submission captures one set of values</li>
                        </ul>
                      </div>
                      <div>
                        <p className="font-medium text-blue-800 mb-1">Table/Grid Layout (Items as Rows):</p>
                        <ul className="list-disc ml-4 space-y-0.5 text-blue-700">
                          <li>Items are displayed as rows in a table</li>
                          <li>Fields become columns that apply to each item</li>
                          <li>Best for: Inventory tracking, item-based data collection, comparing multiple items</li>
                          <li>Each form submission captures values for multiple items at once</li>
                          <li>Perfect for par level inventory: items (Wine, Liquor) as rows, fields (Current Inventory, Par Level, Status) as columns</li>
                        </ul>
                      </div>
                      <div className="mt-2 pt-2 border-t border-blue-300">
                        <p className="text-blue-800 font-medium">Example Use Cases:</p>
                        <p className="text-blue-700 mt-1">
                          <strong>Standard:</strong> Employee onboarding form, incident report, maintenance request
                        </p>
                        <p className="text-blue-700">
                          <strong>Table:</strong> Daily inventory count, product quality check across items, temperature monitoring for multiple locations
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowLayoutHelp(false)}
                      className="mt-2 text-blue-600 hover:text-blue-800 underline text-xs"
                    >
                      Close help
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Translation Status Indicator */}
          {isTranslating && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Translating form to Spanish...</span>
              </div>
            </div>
          )}

          {/* Layout Type Info Banner (shows when table is selected) */}
          {formData.layoutType === 'table' && !showLayoutHelp && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-800">
                <strong>Table Layout Selected:</strong> Items will be rows, fields will be columns. Perfect for inventory tracking with multiple items.
              </p>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
              rows={2}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
            />
            
            {/* Reference Documents */}
            <div className="mt-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Reference Documents (Optional)
              </label>
              <label
                className={`flex items-center justify-center w-full px-2 py-2 border-2 border-dashed rounded cursor-pointer transition-colors ${
                  isDraggingOver
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-primary-500 hover:bg-gray-50'
                }`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center">
                  {uploadingReference ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mb-1"></div>
                  ) : (
                    <Upload className="w-4 h-4 text-gray-500 mb-1" />
                  )}
                  <span className="text-xs text-gray-600">
                    {uploadingReference ? 'Uploading...' : isDraggingOver ? 'Drop files here' : 'Upload PDF/Images or drag and drop'}
                  </span>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,image/*"
                  multiple
                  onChange={handleReferenceDocumentUpload}
                  disabled={uploadingReference}
                />
              </label>
              {formData.referenceDocuments && formData.referenceDocuments.length > 0 && (
                <div className="space-y-1 mt-1.5 max-h-32 overflow-y-auto">
                  {formData.referenceDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-1.5 bg-blue-50 rounded border border-blue-200 text-xs">
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        {doc.type === 'pdf' ? (
                          <FileText className="w-3 h-3 text-red-600 flex-shrink-0" />
                        ) : (
                          <Image className="w-3 h-3 text-green-600 flex-shrink-0" />
                        )}
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-medium text-blue-700 hover:text-blue-900 truncate"
                          title={doc.name}
                        >
                          {doc.name}
                        </a>
                        <span className="text-gray-500 flex-shrink-0">({doc.type.toUpperCase()})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeReferenceDocument(index)}
                        className="text-red-600 hover:text-red-800 p-0.5 flex-shrink-0"
                        title="Remove"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Variables */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Variables (Optional) - Add possible values for each variable
            </label>
            {/* Add New Variable */}
            <div className="flex gap-1.5 mb-1.5">
              <input
                type="text"
                value={newVariableName}
                onChange={(e) => setNewVariableName(e.target.value)}
                onKeyPress={handleVariableKeyPress}
                placeholder="e.g., Room Number"
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
              <button
                type="button"
                onClick={addVariable}
                className="btn-primary px-2 py-1.5 text-sm"
                disabled={!newVariableName.trim()}
              >
                Add
              </button>
            </div>

            {/* Variables List */}
            {formData.variables && formData.variables.length > 0 && (
              <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                {formData.variables.map((variable, index) => (
                  <div key={index} className="bg-gray-50 rounded p-2 border border-gray-200">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 flex-1">
                        <button
                          type="button"
                          onClick={() => toggleVariableExpanded(index)}
                          className="text-xs text-gray-600 hover:text-gray-800"
                          title={expandedVariables.has(index) ? "Collapse" : "Expand to add values"}
                        >
                          {expandedVariables.has(index) ? '▼' : '▶'} {variable.name}
                        </button>
                        {variable.possibleValues.length > 0 && (
                          <span className="text-xs text-gray-500">
                            ({variable.possibleValues.length} value{variable.possibleValues.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeVariable(index)}
                        className="text-red-600 hover:text-red-800 text-xs"
                        title="Remove variable"
                      >
                        ×
                      </button>
                    </div>

                    {expandedVariables.has(index) && (
                      <div className="mt-2 space-y-1.5 pl-4 border-l-2 border-gray-300">
                        {/* Add Value Input */}
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={newVariableValue[index] || ''}
                            onChange={(e) => setNewVariableValue({ ...newVariableValue, [index]: e.target.value })}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                addVariableValue(index);
                              }
                            }}
                            placeholder={`Add value for ${variable.name}...`}
                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                          <button
                            type="button"
                            onClick={() => addVariableValue(index)}
                            className="btn-primary px-2 py-1 text-xs"
                            disabled={!newVariableValue[index]?.trim()}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Values List */}
                        {variable.possibleValues.length > 0 && (
                          <div className="space-y-1">
                            {variable.possibleValues.map((value, valueIndex) => (
                              <div key={valueIndex} className="flex items-center justify-between bg-white px-2 py-1 rounded text-xs border border-gray-200">
                                <span className="text-gray-700">{value}</span>
                                <button
                                  type="button"
                                  onClick={() => removeVariableValue(index, valueIndex)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Standard Form Fields (only show for standard layout) */}
          {formData.layoutType === 'standard' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Form Fields *</label>
            
            {/* Add New Field */}
            <div className="flex gap-1.5 mb-1.5">
              <input
                type="text"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    addField();
                  }
                }}
                placeholder="Enter field label"
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
              <select
                value={newFieldType}
                onChange={(e) => setNewFieldType(e.target.value as FormField['type'])}
                className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
              >
                {fieldTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={addField}
                className="btn-primary px-2 py-1.5 text-sm"
                disabled={!newFieldLabel.trim()}
              >
                Add
              </button>
            </div>

            {/* Fields List */}
            <div className="space-y-2 border border-gray-300 rounded-md p-2">
              {formData.fields?.map((field, index) => (
                <div key={index} className="bg-gray-50 p-2 rounded border border-gray-200">
                  <div className="flex items-start gap-2 mb-1.5">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-500 w-4 text-xs">{index + 1}.</span>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => updateField(index, { label: e.target.value })}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                          placeholder="Field label"
                        />
                        <select
                          value={field.type}
                          onChange={(e) => {
                            const newType = e.target.value as FormField['type'];
                            const updates: Partial<FormField> = { type: newType };
                            if (newType === 'select' || newType === 'radio' || newType === 'checkbox') {
                              updates.options = field.options || [];
                            } else {
                              updates.options = undefined;
                            }
                            // Clear itemReferenceValues if changing away from reference type
                            if (newType !== 'reference') {
                              updates.itemReferenceValues = undefined;
                            }
                            // Clear formula if changing away from calculated type
                            if (newType !== 'calculated') {
                              updates.formula = undefined;
                            }
                            updateField(index, updates);
                          }}
                          className="px-2 py-1 text-xs border border-gray-300 rounded"
                        >
                          {fieldTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={(e) => updateField(index, { required: e.target.checked })}
                            className="w-3 h-3"
                          />
                          Required
                        </label>
                      </div>
                      
                      {/* Field-specific options */}
                      {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                        <div className="ml-6 mt-1 space-y-1">
                          <div className="text-xs text-gray-600 mb-1">Options:</div>
                          <div className="space-y-1">
                            {(field.options || []).map((option, optIdx) => (
                              <div key={optIdx} className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => {
                                    const updatedOptions = [...(field.options || [])];
                                    updatedOptions[optIdx] = e.target.value;
                                    updateField(index, { options: updatedOptions });
                                  }}
                                  className="flex-1 px-2 py-0.5 text-xs border border-gray-300 rounded"
                                  placeholder="Option value"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeFieldOption(index, optIdx)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <div className="flex gap-1 mt-1">
                              <input
                                type="text"
                                value={newFieldOption[index] || ''}
                                onChange={(e) => setNewFieldOption({ ...newFieldOption, [index]: e.target.value })}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    addFieldOption(index);
                                  }
                                }}
                                placeholder="Enter option value..."
                                className="flex-1 px-2 py-0.5 text-xs border border-gray-300 rounded"
                              />
                              <button
                                type="button"
                                onClick={() => addFieldOption(index)}
                                className="btn-primary px-2 py-0.5 text-xs"
                                disabled={!newFieldOption[index]?.trim()}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Reference value input for reference type */}
                      {field.type === 'reference' && (
                        <div className="ml-6 mt-1 space-y-1">
                          <div className="text-xs text-gray-600 mb-1">Fixed Value (Required):</div>
                          <input
                            type="number"
                            value=""
                            disabled
                            placeholder="Reference fields are only supported in table forms"
                            className="w-full px-2 py-0.5 text-xs border border-gray-300 rounded"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-0.5">
                            This value will be shown read-only when the form is filled out
                          </p>
                        </div>
                      )}

                      {/* Formula input for calculated type */}
                      {field.type === 'calculated' && (
                        <div className="ml-6 mt-1 space-y-1">
                          {/* Inline Help Panel */}
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mb-2">
                            <div className="flex items-start gap-2">
                              <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 text-xs">
                                <p className="font-medium text-blue-900 mb-1">How to Write Formulas</p>
                                <ul className="list-disc ml-4 space-y-0.5 text-blue-800 mb-2">
                                  <li>Reference fields by their exact label (e.g., "Current Inventory")</li>
                                  <li>Use operators: +, -, *, /, &lt;, &gt;, ==</li>
                                  <li>Use functions: IF, MAX, MIN, ABS</li>
                                  <li>Text values must be in quotes: "Below Par"</li>
                                </ul>
                                <button 
                                  type="button"
                                  onClick={() => setShowFormulaExamples(index)}
                                  className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                >
                                  View Examples & Full Guide →
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 mb-1">
                            <div className="text-xs text-gray-600">Formula (Required):</div>
                          </div>
                          
                          {/* Show available field labels */}
                          {formData.fields && formData.fields.filter(f => f.label && f.type !== 'calculated').length > 0 && (
                            <div className="mb-1">
                              <div className="text-xs text-gray-500 mb-1">Available fields:</div>
                              <div className="flex flex-wrap gap-1">
                                {formData.fields
                                  .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
                                  .map(f => (
                                    <button
                                      key={f.label}
                                      type="button"
                                      onClick={() => {
                                        const currentFormula = field.formula || '';
                                        const cursorPos = (document.activeElement as HTMLTextAreaElement)?.selectionStart || currentFormula.length;
                                        const newFormula = currentFormula.slice(0, cursorPos) + f.label + currentFormula.slice(cursorPos);
                                        updateField(index, { formula: newFormula });
                                        // Focus back on textarea
                                        setTimeout(() => {
                                          const textarea = document.querySelector(`textarea[data-field-index="${index}"]`) as HTMLTextAreaElement;
                                          if (textarea) {
                                            textarea.focus();
                                            const newPos = cursorPos + f.label.length;
                                            textarea.setSelectionRange(newPos, newPos);
                                          }
                                        }, 0);
                                      }}
                                      className="text-xs px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 cursor-pointer"
                                      title="Click to insert into formula"
                                    >
                                      "{f.label}"
                                    </button>
                                  ))}
                              </div>
                            </div>
                          )}
                          
                          <textarea
                            data-field-index={index}
                            value={field.formula || ''}
                            onChange={(e) => {
                              updateField(index, { formula: e.target.value });
                              // Validate formula in real-time
                              validateFormula(index, e.target.value);
                            }}
                            placeholder='Example: IF(Current Inventory < Par Level, "Below Par", "OK")'
                            className={`w-full px-2 py-1 text-xs border rounded font-mono ${
                              formulaErrors[index] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            rows={3}
                            required
                          />
                          
                          {/* Formula Validation Messages */}
                          {formulaErrors[index] && (
                            <p className="text-xs text-red-600 mt-0.5">
                              ⚠️ {formulaErrors[index]}
                            </p>
                          )}
                          {!formulaErrors[index] && field.formula && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-green-600">
                                ✓ Formula looks good
                              </p>
                              <button
                                type="button"
                                onClick={() => setShowFormulaTest(index)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                🧪 Test with Sample Values
                              </button>
                            </div>
                          )}
                          
                          {!formulaErrors[index] && !field.formula && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Reference fields by their exact label. Supports: +, -, *, /, IF, MAX, MIN, &lt;, &gt;, ==
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Formula Examples Modal */}
                      {showFormulaExamples === index && (
                        <FormulaExamplesModal
                          isOpen={true}
                          onClose={() => setShowFormulaExamples(null)}
                          availableFields={formData.fields
                            ?.filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
                            .map(f => ({ label: f.label, type: f.type })) || []}
                        />
                      )}
                      
                      {/* Formula Test Modal */}
                      {showFormulaTest === index && (
                        <FormulaTestModal
                          isOpen={true}
                          onClose={() => setShowFormulaTest(null)}
                          formula={field.formula || ''}
                          fields={formData.fields || []}
                          fieldIndex={index}
                        />
                      )}

                      {/* Placeholder and default value */}
                      {(field.type === 'text' || field.type === 'email' || field.type === 'tel' || field.type === 'number') && (
                        <div className="ml-6 mt-1 space-y-1">
                          <input
                            type="text"
                            value={field.placeholder || ''}
                            onChange={(e) => updateField(index, { placeholder: e.target.value })}
                            placeholder="Placeholder text (optional)"
                            className="w-full px-2 py-0.5 text-xs border border-gray-300 rounded"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-0.5">
                      <button
                        type="button"
                        onClick={() => moveField(index, 'up')}
                        disabled={index === 0}
                        className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                        title="Move up"
                      >
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveField(index, 'down')}
                        disabled={index === (formData.fields?.length || 0) - 1}
                        className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                        title="Move down"
                      >
                        <ChevronDown className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeField(index)}
                        className="text-red-600 hover:text-red-800 px-1"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {(!formData.fields || formData.fields.length === 0) && (
                <div className="text-center py-3 text-gray-500 text-xs">
                  No fields added yet
                </div>
              )}
            </div>
          </div>
          )}

          {/* Table Form Configuration (only show for table layout) */}
          {formData.layoutType === 'table' && (
            <>
              {/* Items Definition */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Items (Rows) *
                </label>
                <div className="space-y-2">
                  {/* AI Extraction Option */}
                  {isGeminiConfigured() && (
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium text-blue-900">AI-Powered Item Extraction</span>
                      </div>
                      <p className="text-xs text-blue-800 mb-2">
                        Upload a document (PDF, Word, or image) and AI will automatically extract item names for you.
                      </p>
                      <label className="flex items-center justify-center w-full px-2 py-2 border-2 border-dashed border-blue-300 rounded cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-100">
                        {isExtractingItems ? (
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-blue-600 animate-spin" />
                            <span className="text-xs text-blue-700">Extracting items...</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Upload className="w-4 h-4 text-blue-600" />
                            <span className="text-xs text-blue-700">Upload Document to Extract Items</span>
                          </div>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={handleExtractItemsFromFile}
                          disabled={isExtractingItems}
                        />
                      </label>
                    </div>
                  )}

                  {/* Extracted Items Preview */}
                  {showExtractedItems && extractedItems.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-medium text-green-900">
                            Found {extractedItems.length} item{extractedItems.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={handleDiscardExtractedItems}
                          className="text-green-700 hover:text-green-900"
                          title="Discard"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      {extractionNotes && (
                        <p className="text-xs text-green-800 mb-2 italic">{extractionNotes}</p>
                      )}
                      <div className="max-h-32 overflow-y-auto border border-green-200 rounded bg-white p-2 mb-2">
                        <div className="flex flex-wrap gap-1">
                          {extractedItems.map((item, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded border border-green-300">
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleUseExtractedItems}
                        className="w-full px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium"
                      >
                        Add {extractedItems.length} Item{extractedItems.length !== 1 ? 's' : ''} to List
                      </button>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newItemName}
                      onChange={(e) => setNewItemName(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          addTableItem();
                        }
                      }}
                      placeholder="Enter item name (e.g., Wine)"
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    />
                    <button
                      type="button"
                      onClick={addTableItem}
                      className="btn-primary px-3 py-1.5 text-sm"
                      disabled={!newItemName.trim()}
                    >
                      <Plus className="w-4 h-4" /> Add Item
                    </button>
                  </div>
                  {formData.itemsDefinition?.items && formData.itemsDefinition.items.length > 0 && (
                    <div className="space-y-1 max-h-64 overflow-y-auto border border-gray-300 rounded-md p-2">
                      {formData.itemsDefinition.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-1.5 bg-gray-50 p-1.5 rounded text-xs">
                          <span className="font-medium text-gray-500 w-4">{idx + 1}.</span>
                          <span className="flex-1 text-gray-700">{item}</span>
                          <div className="flex gap-0.5">
                            <button
                              type="button"
                              onClick={() => moveTableItem(idx, 'up')}
                              disabled={idx === 0}
                              className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                              title="Move up"
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveTableItem(idx, 'down')}
                              disabled={idx === (formData.itemsDefinition?.items?.length || 0) - 1}
                              className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                              title="Move down"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeTableItem(idx)}
                              className="text-red-600 hover:text-red-800 px-1"
                              title="Remove"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Table Fields (Columns) */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Table Fields (Columns) * - These apply to each item
                </label>
                <p className="text-xs text-gray-500 italic mb-2">
                  Each item will have a row with these fields as columns. Reference and calculated fields are supported.
                </p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTableFieldLabel}
                      onChange={(e) => setNewTableFieldLabel(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          addTableField();
                        }
                      }}
                      placeholder="Field label (e.g., Current Inventory)"
                      className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    />
                    <select
                      value={newTableFieldType}
                      onChange={(e) => setNewTableFieldType(e.target.value as FormField['type'])}
                      className="px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    >
                      {fieldTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addTableField}
                      className="btn-primary px-2 py-1.5 text-sm"
                      disabled={!newTableFieldLabel.trim()}
                    >
                      Add
                    </button>
                  </div>
                  {formData.tableFields && formData.tableFields.length > 0 && (
                    <div className="space-y-2 border border-gray-300 rounded-md p-2">
                      {formData.tableFields.map((field, index) => (
                        <div key={index} className="bg-gray-50 p-2 rounded border border-gray-200">
                          <div className="flex items-start gap-2 mb-1.5">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-gray-500 w-4 text-xs">{index + 1}.</span>
                                <input
                                  type="text"
                                  value={field.label}
                                  onChange={(e) => updateTableField(index, { label: e.target.value })}
                                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                  placeholder="Field label"
                                />
                                <select
                                  value={field.type}
                                  onChange={(e) => {
                                    const newType = e.target.value as FormField['type'];
                                    const updates: Partial<FormField> = { type: newType };
                                    if (newType === 'select' || newType === 'radio' || newType === 'checkbox') {
                                      updates.options = field.options || [];
                                    } else {
                                      updates.options = undefined;
                                    }
                                    if (newType !== 'reference') {
                                      updates.itemReferenceValues = undefined;
                                    }
                                    if (newType !== 'calculated') {
                                      updates.formula = undefined;
                                    }
                                    updateTableField(index, updates);
                                  }}
                                  className="px-2 py-1 text-xs border border-gray-300 rounded"
                                >
                                  {fieldTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                  ))}
                                </select>
                                <label className="flex items-center gap-1 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={field.required || false}
                                    onChange={(e) => updateTableField(index, { required: e.target.checked })}
                                    className="w-3 h-3"
                                  />
                                  Required
                                </label>
                              </div>
                              
                              {/* Reference field value input - per item */}
                              {field.type === 'reference' && formData.itemsDefinition?.items && formData.itemsDefinition.items.length > 0 && (
                                <div className="ml-6 mt-2 space-y-1">
                                  <div className="text-xs font-medium text-gray-600 mb-1">Reference Values (per item):</div>
                                  <div className="border border-gray-300 rounded p-2 bg-white space-y-1.5">
                                    {formData.itemsDefinition.items.map((item, itemIdx) => (
                                      <div key={itemIdx} className="flex items-center gap-2">
                                        <label className="text-xs text-gray-700 w-20 flex-shrink-0">{item}:</label>
                                        <input
                                          type="text"
                                          value={field.itemReferenceValues?.[item] !== undefined ? String(field.itemReferenceValues[item]) : ''}
                                          onChange={(e) => {
                                            const inputValue = e.target.value.trim();
                                            const value = inputValue === '' ? undefined : (isNaN(Number(inputValue)) ? inputValue : Number(inputValue));
                                            const currentItemValues = field.itemReferenceValues || {};
                                            const newItemValues: Record<string, string | number> = {};
                                            // Copy existing values (excluding undefined)
                                            Object.entries(currentItemValues).forEach(([key, val]) => {
                                              if (val !== undefined) {
                                                newItemValues[key] = val;
                                              }
                                            });
                                            // Add/update new value if not undefined
                                            if (value !== undefined) {
                                              newItemValues[item] = value;
                                            } else {
                                              // Remove the item if value is undefined (empty)
                                              delete newItemValues[item];
                                            }
                                            updateTableField(index, { 
                                              itemReferenceValues: newItemValues
                                            });
                                          }}
                                          placeholder="Enter value (text or number)"
                                          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                          required
                                        />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Calculated field formula input */}
                              {field.type === 'calculated' && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {/* Inline Help Panel */}
                                  <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mb-2">
                                    <div className="flex items-start gap-2">
                                      <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                      <div className="flex-1 text-xs">
                                        <p className="font-medium text-blue-900 mb-1">How to Write Formulas</p>
                                        <ul className="list-disc ml-4 space-y-0.5 text-blue-800 mb-2">
                                          <li>Reference fields by their exact label (e.g., "Current Inventory")</li>
                                          <li>Use operators: +, -, *, /, &lt;, &gt;, ==</li>
                                          <li>Use functions: IF, MAX, MIN, ABS</li>
                                          <li>Text values must be in quotes: "Below Par"</li>
                                        </ul>
                                        <button 
                                          type="button"
                                          onClick={() => setShowFormulaExamples(`table-${index}`)}
                                          className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                        >
                                          View Examples & Full Guide →
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1 mb-1">
                                    <div className="text-xs text-gray-600">Formula (Required):</div>
                                  </div>
                                  
                                  {/* Show available table field labels */}
                                  {formData.tableFields && formData.tableFields.filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0)).length > 0 && (
                                    <div className="mb-1">
                                      <div className="text-xs text-gray-500 mb-1">Available fields:</div>
                                      <div className="flex flex-wrap gap-1">
                                        {formData.tableFields
                                          .filter(f => f.label && f.type !== 'calculated' && f.order < (field.order || 0))
                                          .map(f => (
                                            <button
                                              key={f.label}
                                              type="button"
                                              onClick={() => {
                                                const currentFormula = field.formula || '';
                                                const cursorPos = (document.activeElement as HTMLTextAreaElement)?.selectionStart || currentFormula.length;
                                                const newFormula = currentFormula.slice(0, cursorPos) + f.label + currentFormula.slice(cursorPos);
                                                updateTableField(index, { formula: newFormula });
                                                // Focus back on textarea
                                                setTimeout(() => {
                                                  const textarea = document.querySelector(`textarea[data-table-field-index="${index}"]`) as HTMLTextAreaElement;
                                                  if (textarea) {
                                                    textarea.focus();
                                                    const newPos = cursorPos + f.label.length;
                                                    textarea.setSelectionRange(newPos, newPos);
                                                  }
                                                }, 0);
                                              }}
                                              className="text-xs px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 cursor-pointer"
                                              title="Click to insert into formula"
                                            >
                                              "{f.label}"
                                            </button>
                                          ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  <textarea
                                    data-table-field-index={index}
                                    value={field.formula || ''}
                                    onChange={(e) => {
                                      updateTableField(index, { formula: e.target.value });
                                      // Validate formula
                                      if (formData.tableFields) {
                                        validateTableFormula(index, e.target.value);
                                      }
                                    }}
                                    placeholder='Example: IF(Current Inventory < Par Level, "Below Par", "OK")'
                                    className={`w-full px-2 py-1 text-xs border rounded font-mono ${
                                      formulaErrors[`table-${index}`] ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                    }`}
                                    rows={3}
                                    required
                                  />
                                  
                                  {/* Formula Validation Messages */}
                                  {formulaErrors[`table-${index}`] && (
                                    <p className="text-xs text-red-600 mt-0.5">
                                      ⚠️ {formulaErrors[`table-${index}`]}
                                    </p>
                                  )}
                                  {!formulaErrors[`table-${index}`] && field.formula && (
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <p className="text-xs text-green-600">
                                        ✓ Formula looks good
                                      </p>
                                      <button
                                        type="button"
                                        onClick={() => setShowFormulaTest(`table-${index}`)}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                                      >
                                        🧪 Test with Sample Values
                                      </button>
                                    </div>
                                  )}
                                  
                                  {!formulaErrors[`table-${index}`] && !field.formula && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      Reference fields by their exact label. Supports: +, -, *, /, IF, MAX, MIN, &lt;, &gt;, ==
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex gap-0.5">
                              <button
                                type="button"
                                onClick={() => moveTableField(index, 'up')}
                                disabled={index === 0}
                                className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                                title="Move up"
                              >
                                <ChevronUp className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveTableField(index, 'down')}
                                disabled={index === (formData.tableFields?.length || 0) - 1}
                                className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                                title="Move down"
                              >
                                <ChevronDown className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeTableField(index)}
                                className="text-red-600 hover:text-red-800 px-1"
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(!formData.tableFields || formData.tableFields.length === 0) && (
                    <div className="text-center py-3 text-gray-500 text-xs border border-gray-300 rounded">
                      No table fields added yet
                    </div>
                  )}
                </div>
              </div>
              
              {/* Table Field Formula Examples Modal */}
              {typeof showFormulaExamples === 'string' && showFormulaExamples.startsWith('table-') && (
                (() => {
                  const tableIndex = parseInt(showFormulaExamples.replace('table-', ''));
                  const tableField = formData.tableFields?.[tableIndex];
                  if (!tableField) return null;
                  return (
                    <FormulaExamplesModal
                      isOpen={true}
                      onClose={() => setShowFormulaExamples(null)}
                      availableFields={formData.tableFields
                        ?.filter(f => f.label && f.type !== 'calculated' && f.order < (tableField.order || 0))
                        .map(f => ({ label: f.label, type: f.type })) || []}
                    />
                  );
                })()
              )}
              
              {/* Table Field Formula Test Modal */}
              {typeof showFormulaTest === 'string' && showFormulaTest.startsWith('table-') && (
                (() => {
                  const tableIndex = parseInt(showFormulaTest.replace('table-', ''));
                  const tableField = formData.tableFields?.[tableIndex];
                  if (!tableField) return null;
                  // Calculate the index in the tableFields array
                  const combinedFields = formData.tableFields || [];
                  const combinedIndex = tableIndex;
                  return (
                    <FormulaTestModal
                      isOpen={true}
                      onClose={() => setShowFormulaTest(null)}
                      formula={tableField.formula || ''}
                      fields={combinedFields}
                      fieldIndex={combinedIndex}
                    />
                  );
                })()
              )}
            </>
          )}
        </form>
      </div>

      {/* Footer with buttons */}
      <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary px-4 py-2 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          form="form-form"
          className="btn-primary px-4 py-2 text-sm"
          disabled={Object.keys(formulaErrors).length > 0 || (formData.layoutType === 'table' && Object.keys(formulaErrors).some(k => k.startsWith('table-')))}
        >
          {form ? 'Update' : 'Create'} Form
        </button>
      </div>

      {/* Inline Template Creation Modal */}
      {isCreatingTemplate && (
        <InlineTemplateForm
          onSave={(template) => {
            // Reload templates to include the new one
            loadTemplates();
            // Auto-select the newly created template
            setFormData({ ...formData, templateId: template.id || '' });
            setIsCreatingTemplate(false);
          }}
          onCancel={() => setIsCreatingTemplate(false)}
          categories={categories.length > 0 ? categories : ['Fire Safety', 'Life Safety', 'Health & Safety', 'Equipment Maintenance', 'Building Maintenance', 'Environmental', 'Security', 'Other']}
          suggestedName={formData.name ? `${formData.name} Template` : ''}
        />
      )}
    </div>
  );
};

export default FormForm;

